import memoize from 'memoizee'
import type { Chain } from 'viem'
import { createConfig, type Transport, type CreateConnectorFn, type Config } from '@wagmi/core'
import { connectors as defaultConnectors } from './connectors'

declare module 'wagmi' {
  /** Enable Wagmi to infer types in places that wouldn't normally have access to type info via React Context alone. */
  // eslint-disable-next-line @typescript-eslint/consistent-type-definitions
  interface Register {
    config: ReturnType<typeof createWagmiConfig>
  }
}

/** Singleton reference to the wagmi config instance */
let _config: Config | undefined

/**
 * Returns the current wagmi config instance for use outside React context, like in `queryFn`.
 *
 * **Why this exists:**
 * TanStack Query's `queryFn` runs outside the React component tree, so it cannot
 * use hooks like `useConfig()` from wagmi. This singleton provides imperative access
 * to the wagmi config for on-chain reads (e.g., `readContract`) within query functions.
 *
 * **Example use case:**
 * Fetching the sreusd -> reusd exchange rate via `readContract` as part of the
 * token USD rate query's fallback logic.
 *
 * Alternatives considered:
 * - Splitting queries: doesn't scale well with our combined usage of `getQueryData`,
 *   `useQuery`, `fetchQuery`, and `getQueryOptions` via `queryFactory`
 * - Passing config through query params: you can't pass complex non-serializable objects from classes
 * - Importing or creating a config inside `queryFn` is not possible due its dependencies and complex factory function
 */
export const getWagmiConfig = () => _config

type CreateWagmiConfigOptions<TChains extends readonly [Chain, ...Chain[]]> = {
  /** An array of Chain objects to configure */
  chains: TChains
  /** A record mapping chain IDs to their respective transport configurations */
  transports: Record<TChains[number]['id'], Transport>
  /** Optional record of connector functions (defaults to defaultConnectors) */
  connectors?: CreateConnectorFn[]
}

/**
 * Creates a Wagmi configuration based on chains, transports and connectors.
 * We use memoize here to ensure only one config instance exists, preventing issues with wagmi auto-reconnect.
 * @return A Wagmi configuration object that includes chains, connectors, and transports.
 */
export const createWagmiConfig = memoize(
  <TChains extends readonly [Chain, ...Chain[]]>({
    chains,
    transports,
    connectors = defaultConnectors,
  }: CreateWagmiConfigOptions<TChains>) => {
    _config = createConfig({ chains, connectors, transports })
    return _config
  },
  {
    max: 1, // only memoize the last call
    normalizer: ([{ chains, transports }]) =>
      JSON.stringify({
        chainIds: chains.map(c => c.id),
        transportIds: Object.keys(transports),
        rpcUrls: chains.flatMap(c => c.rpcUrls?.default?.http ?? []),
      }),
  },
)

/**
 * When running tests, we need to reset the wagmi config between tests to prevent issues with auto-reconnect and memoization.
 * This function clears the memoized config and resets the singleton reference.
 * This function is put here so we don't need to expose the _config variable.
 */
export const resetWagmiConfigForTests = () => {
  createWagmiConfig.clear()
  // otherwise wagmi config is memoized and breaks reconnect on tests
  _config = undefined
}
